import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    Alert
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import FeedbackIcon from '@mui/icons-material/Feedback';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import FeedbackApi from "../../../apis/feedback/feedback-api";
import useUser from "../../../custom-hook/use-user";
import packageJson from '../../../../package.json';

function FeedbackDialog({ open, onClose }) {
    const authUser = useSelector((state) => state.authUser);
    const { getActiveUser, getToken, getUserInstallationId, getCompanyId } = useUser();
    const location = useLocation();
    const activeUser = getActiveUser();

    const [formData, setFormData] = useState({
        type: 'bug',
        title: '',
        description: '',
        imageUrls: []
    });
    const [imageUrlInput, setImageUrlInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        setError(null);
    };

    const handleSubmit = async () => {
        // Validate
        if (!formData.title.trim()) {
            setError('Please enter a title');
            return;
        }
        if (formData.imageUrls.length > 5) {
            setError('Please attach no more than 5 images.');
            return;
        }
        if (!formData.description.trim()) {
            setError('Please enter a description');
            return;
        }

        setLoading(true);
        setError(null);

        // Build context
        const context = {
            email: authUser?.userName || '',
            firstName: authUser?.firstName || '',
            lastName: authUser?.lastName || '',
            companyId: getCompanyId() || '',
            companyName: activeUser?.companyName || '',
            installationId: getUserInstallationId() || '',
            currentPath: location.pathname,
            userAgent: navigator.userAgent,
            appVersion: packageJson.version
        };

        try {
            await FeedbackApi.submitFeedback({
                type: formData.type,
                title: formData.title,
                description: formData.description,
                imageUrls: formData.imageUrls,
                context
            }, getToken());

            setSuccess(true);
            // Reset form after short delay
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err) {
            setError(err?.errors?.[0]?.displayMessage || 'Failed to submit feedback. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({ type: 'bug', title: '', description: '', imageUrls: [] });
            setImageUrlInput('');
            setError(null);
            setSuccess(false);
            onClose();
        }
    };

    const handleAddImageUrl = () => {
        const url = imageUrlInput.trim();
        if (!url) return;
        try {
            // Throws on invalid URL
            new URL(url);
        } catch (_e) {
            setError('Please enter a valid image URL.');
            return;
        }
        setFormData((prev) => ({
            ...prev,
            imageUrls: prev.imageUrls.includes(url) ? prev.imageUrls : [...prev.imageUrls, url]
        }));
        setImageUrlInput('');
        setError(null);
    };

    const handleRemoveImageUrl = (urlToRemove) => {
        setFormData((prev) => ({
            ...prev,
            imageUrls: prev.imageUrls.filter((url) => url !== urlToRemove)
        }));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #E0E0E0',
                pb: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FeedbackIcon sx={{ color: '#1976D2' }} />
                    <Typography variant="h6" fontWeight="600">
                        Submit Feedback
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" disabled={loading}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5 }}>
                {success ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Thank you! Your feedback has been submitted successfully.
                    </Alert>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        {/* Type Selection */}
                        <FormControl>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                What would you like to report?
                            </Typography>
                            <RadioGroup
                                row
                                value={formData.type}
                                onChange={handleChange('type')}
                            >
                                <FormControlLabel
                                    value="bug"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <BugReportIcon sx={{ fontSize: 20, color: '#E65100' }} />
                                            <span>Bug Report</span>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="feature"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <LightbulbIcon sx={{ fontSize: 20, color: '#1565C0' }} />
                                            <span>Feature Request</span>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>

                        {/* Title */}
                        <TextField
                            label="Title"
                            placeholder={formData.type === 'bug'
                                ? "Brief summary of the issue"
                                : "Brief summary of your idea"
                            }
                            value={formData.title}
                            onChange={handleChange('title')}
                            fullWidth
                            required
                            disabled={loading}
                            inputProps={{ maxLength: 100 }}
                        />

                        {/* Description */}
                        <TextField
                            label="Description"
                            placeholder={formData.type === 'bug'
                                ? "What happened? What did you expect to happen?"
                                : "Describe the feature and how it would help you"
                            }
                            value={formData.description}
                            onChange={handleChange('description')}
                            fullWidth
                            multiline
                            rows={4}
                            required
                            disabled={loading}
                            inputProps={{ maxLength: 5000 }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Images (optional)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    label="Image URL"
                                    placeholder="https://..."
                                    value={imageUrlInput}
                                    onChange={(event) => setImageUrlInput(event.target.value)}
                                    fullWidth
                                    disabled={loading}
                                />
                                <Button variant="outlined" onClick={handleAddImageUrl} disabled={loading}>
                                    Add
                                </Button>
                            </Box>
                            {formData.imageUrls.map((url) => (
                                <Box key={url} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>{url}</Typography>
                                    <Button size="small" color="error" onClick={() => handleRemoveImageUrl(url)} disabled={loading}>
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                        </Box>

                        {/* Context Info (read-only) */}
                        <Box sx={{
                            backgroundColor: '#F5F5F5',
                            p: 1.5,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                        }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                The following context will be included automatically:
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                Page: {location.pathname}<br />
                                App Version: {packageJson.version}
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error">
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            {!success && (
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : null}
                    >
                        {loading ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

export default FeedbackDialog;
